<script setup lang="ts">
import EditableNotesField from '@/components/rc/common/EditableNotesField.vue'
import FailureTypeSelect from '@/components/rc/miner-repairs/repairs-sidebar/diagnostics-elements/FailureTypeSelect.vue'
import { useRepairsContext } from '@/composables/rc/drawer-context'

import { useFailureTypes } from '@/composables/rc/failure-types'
import { useMinerRepairActions, type UpdatePartMutation } from '@/composables/rc/miner-repair-actions'
import { usePartDetails } from '@/composables/rc/part-details'

import { PartDiagnosticStatus } from '@/types/rc/miners/PartStatus'

import { Icon } from '@iconify/vue'
import { computed, ref, watch } from 'vue'

const { minerId, selectedPart } = useRepairsContext()

// Context - BE
const {
  isPending,
  partId,
  hasFailure,
  isReplacement,
  movedToInventory,
  failureReason,
  initialDiagnostic,
  failureTypeId,
  asicN,
  partType,
} = usePartDetails(minerId, selectedPart)

// State
const selectedPartInitialDiagnostic = ref<PartDiagnosticStatus>()
const selectedFailureType = ref<number>()
const setFailureReason = ref<string>()
const setAsicN = ref(1)
const editing = ref(false)

const setStateToBEData = () => {
  selectedPartInitialDiagnostic.value = initialDiagnostic.value
  selectedFailureType.value = failureTypeId.value
  setAsicN.value = asicN.value
  editing.value = false
}

// Bind state to source data
// Watch minerId and selectedPart for context changes
// Also watch the actual data values so updates trigger when query is invalidated/refetched
watch([minerId, selectedPart, initialDiagnostic, failureTypeId, asicN], setStateToBEData, { immediate: true })

const { updatePartStatusMutation } = useMinerRepairActions(minerId)
const { mutate: mutatePartStatus } = updatePartStatusMutation

const { isFailureTypeOther, isFailureTypeASICN } = useFailureTypes()

const isFailureTypeOtherSelected = computed(
  () => selectedFailureType.value && isFailureTypeOther(selectedFailureType.value)
)

const isFailureTypeASICNSelected = computed(
  () => selectedFailureType.value && isFailureTypeASICN(selectedFailureType.value)
)

const isDiagnosticEditable = computed(() => {
  return initialDiagnostic.value && !editing.value && !isReplacement.value && !movedToInventory.value
})

/** Proxy partially applying id params to mutation */
const updatePartStatus = (payload: Partial<UpdatePartMutation>) => {
  if (!partId.value) return
  mutatePartStatus(
    { part_id: partId.value, partName: selectedPart.value, ...payload },
    {
      onSettled: () => {
        if (editing.value) editing.value = false
      },
      onError: () => {
        setStateToBEData()
      },
    }
  )
}

// Event handlers

// If initial diagnostic is 'Fail', we need to provide failure type before proceeding
const onChangeInitialDiagnostic = (value: PartDiagnosticStatus) => {
  if (selectedPartInitialDiagnostic.value == PartDiagnosticStatus.Fail) return
  updatePartStatus({ initial_diagnostic_data: { initial_diagnostic: value ?? null } })
}

// If failure type is 'Other' or '# ASIC', we need to input further data before posting
const onChangeFailureType = () => {
  if (isFailureTypeOtherSelected.value || isFailureTypeASICNSelected.value) return
  updatePartStatus({
    initial_diagnostic_data: {
      initial_diagnostic: PartDiagnosticStatus.Fail,
      failure_type_id: selectedFailureType.value,
    },
  })
}

// If failure reason is set, post. Also triggered when in headless mode.
const onSaveFailureReason = (failureText: string) => {
  if (hasFailure.value || !selectedFailureType.value) return
  updatePartStatus({
    initial_diagnostic_data: {
      initial_diagnostic: PartDiagnosticStatus.Fail,
      failure_type_id: selectedFailureType.value,
      comment: failureText,
    },
  })
}

// Section-wide unlock/edit button at the top
const onSaveEdit = () => {
  updatePartStatus({
    initial_diagnostic_data: {
      initial_diagnostic: selectedPartInitialDiagnostic.value,
      failure_type_id: selectedFailureType.value,
      comment: failureReasonData.value,
    },
  })
}

// The failure reason BE-payload field is used for asicN when it's set
const failureReasonData = computed(() => {
  if (selectedPartInitialDiagnostic.value == PartDiagnosticStatus.Fail && isFailureTypeOtherSelected.value)
    return setFailureReason.value
  if (selectedPartInitialDiagnostic.value == PartDiagnosticStatus.Fail && isFailureTypeASICNSelected.value)
    return setAsicN.value.toString()
  return undefined
})
</script>

<template>
  <!-- Loading state -->
  <template v-if="isPending">
    <el-skeleton :animated="true"
      ><template #template><el-skeleton-item variant="rect" class="h-8" /></template
    ></el-skeleton>
  </template>

  <!-- Missing part state -->
  <template v-else-if="!partId">
    <el-select :disabled="!partId" placeholder="No part to diagnose" />
  </template>

  <!-- All systems go -->
  <template v-else>
    <div class="w-full flex items-center gap-2">
      <el-select
        v-model="selectedPartInitialDiagnostic"
        clearable
        :disabled="initialDiagnostic && !editing"
        @change="onChangeInitialDiagnostic"
      >
        <el-option v-for="status in Object.values(PartDiagnosticStatus)" :key="status" :value="status" />
      </el-select>

      <!-- If we have a failure set, this section is resolved and so it goes into "edit mode", -->
      <!-- where updates to any of its fields are carried over by this unlock/save button -->
      <Icon
        v-if="isDiagnosticEditable"
        class="cursor-pointer"
        icon="material-symbols:edit-outline"
        @click="editing = true"
      />
      <Icon
        v-if="initialDiagnostic && editing"
        class="cursor-pointer"
        icon="material-symbols:check-rounded"
        @click="onSaveEdit"
      />
    </div>

    <!-- Failure type: If it's failing, we need to provide a failure type -->
    <FailureTypeSelect
      v-if="selectedPartInitialDiagnostic == PartDiagnosticStatus.Fail"
      v-model="selectedFailureType"
      :disabled="hasFailure && !editing"
      :part-type="partType"
      @update:model-value="onChangeFailureType"
    />

    <!-- Failure reason: If it's failing and selected failure is type 'Other', we need to provide a failure reason -->
    <EditableNotesField
      v-if="selectedPartInitialDiagnostic == PartDiagnosticStatus.Fail && isFailureTypeOtherSelected"
      v-model="setFailureReason"
      title="Failure reason"
      placeholder="Describe failure"
      :cache-value="failureReason ?? ''"
      :headless="hasFailure"
      :edit="hasFailure && editing"
      @save="onSaveFailureReason"
    />

    <!-- # ASIC: If it's failing and selected failure is type '# ASIC', display a number input for the quantity -->
    <div
      v-if="selectedPartInitialDiagnostic == PartDiagnosticStatus.Fail && isFailureTypeASICNSelected"
      class="flex justify-between w-full"
    >
      <el-input-number v-model="setAsicN" placeholder="# ASIC" :min="1" :disabled="hasFailure && !editing" />
      <el-button
        v-if="!hasFailure"
        type="primary"
        :disabled="hasFailure && setAsicN == Number(asicN)"
        @click="onSaveFailureReason(setAsicN.toString())"
        >Save</el-button
      >
    </div>
  </template>
</template>
